import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, mergeStyles } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { CouponFrontdoor, CampaignAutobiddingSelection, CompletionConfirmation } from './steps';
import { useHideCalloutPreference, useAutoBiddingFac } from './hooks';

const AUTOBIDDING_COUPON_STEPS = Object.freeze({
  FRONTDOOR: 0,
  CAMPAIGN_SELECTION: 1,
  COMPLETION_CONFIRMATION: 2,
});

const modalStyles = mergeStyles({
  minWidth: 600,
  padding: 24,
});

const overlayStyles = {
  root: {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
};

export const AutoBiddingCouponCallout = () => {
  const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
  const [currentStep, setCurrentStep] = useState(AUTOBIDDING_COUPON_STEPS.FRONTDOOR);
  const [coupon, setCoupon] = useState(null);

  const { getCoupon } = useAutoBiddingFac();
  const serverContext = useContext(ServerContext);
  const { CurrentUser, CurrentAccount } = serverContext;
  const { isCalloutHidden, hideCalloutFor7Days, hideCalloutForEver } = useHideCalloutPreference(CurrentUser.Id, CurrentAccount.Id, coupon);

  const [selectedCampaigns, setSelectedCampaigns] = useState([]);

  useEffect(() => {
    if (coupon === null) {
      getCoupon().then((autoBiddingCoupon) => { setCoupon(autoBiddingCoupon); });
    }
  }, [coupon, getCoupon]);

  useEffect(() => {
    if (coupon !== null && !isCalloutHidden) {
      showModal();
    } else {
      hideModal();
    }
  }, [showModal, coupon, hideModal, isCalloutHidden]);

  const hideCalloutFor7DaysOnClose = useCallback(() => {
    hideModal();
    hideCalloutFor7Days();
  }, [hideCalloutFor7Days, hideModal]);

  const hideCalloutForEverOnClose = useCallback(() => {
    hideModal();
    hideCalloutForEver();
  }, [hideCalloutForEver, hideModal]);

  const modalContent = useMemo(() => {
    switch (currentStep) {
      case AUTOBIDDING_COUPON_STEPS.FRONTDOOR:
        return (<CouponFrontdoor
          coupon={coupon}
          onClose={hideModal} // CouponFrontdoor uses hideModal directly because it manages its own closing logic
          onNext={() => setCurrentStep(AUTOBIDDING_COUPON_STEPS.CAMPAIGN_SELECTION)}
        />);
      case AUTOBIDDING_COUPON_STEPS.CAMPAIGN_SELECTION:
        return (<CampaignAutobiddingSelection
          coupon={coupon}
          onClose={hideCalloutFor7DaysOnClose}
          onNext={() => { setCurrentStep(AUTOBIDDING_COUPON_STEPS.COMPLETION_CONFIRMATION); }}
          selectedCampaigns={selectedCampaigns}
          setSelectedCampaigns={setSelectedCampaigns}
        />);
      case AUTOBIDDING_COUPON_STEPS.COMPLETION_CONFIRMATION:
        return <CompletionConfirmation onClose={hideCalloutForEverOnClose} coupon={coupon} selectedCampaigns={selectedCampaigns} />;
      default:
        return null;
    }
  }, [currentStep, coupon, hideModal, hideCalloutFor7DaysOnClose, selectedCampaigns, hideCalloutForEverOnClose]);

  return (
    <Modal
      className="autobidding-coupon-callout"
      isOpen={isModalOpen}
      overlay={{ styles: overlayStyles }}
      isBlocking
    >
      <div className={modalStyles}>
        {modalContent}
      </div>
    </Modal>
  );
};

AutoBiddingCouponCallout.propTypes = {
  i18n: PropTypes.shape({}).isRequired,
};
